import { useCallback, useEffect, useRef } from "react";
import { GridApi, ColumnState } from "ag-grid-community";
import { logger } from "@platform/browser-logger";

import { useStorage } from "./useStorage";

export interface AgGridStateConfig {
  /**
   * Unique identifier for this grid instance.
   * Used as the storage key prefix.
   */
  gridId: string;

  /**
   * Storage type to use for persistence.
   * @default "session"
   */
  storageType?: "local" | "session";

  /**
   * Whether to persist column state (width, position, visibility, etc.).
   * @default true
   */
  persistColumnState?: boolean;

  /**
   * Whether to persist filter state.
   * @default true
   */
  persistFilterState?: boolean;

  /**
   * Whether to persist sort state.
   * @default true
   */
  persistSortState?: boolean;

  /**
   * Whether to persist column group state (expanded/collapsed).
   * @default true
   */
  persistColumnGroupState?: boolean;

  /**
   * Debounce time in milliseconds for saving state.
   * Prevents excessive writes during rapid changes.
   * @default 500
   */
  debounceMs?: number;

  /**
   * Callback invoked when state is restored.
   */
  onStateRestored?: () => void;

  /**
   * Callback invoked when state is saved.
   */
  onStateSaved?: () => void;

  /**
   * Callback invoked when there's an error during state operations.
   */
  onError?: (error: Error) => void;
}

// AG Grid's ColumnGroupState import doesn't match the actual getColumnGroupState/setColumnGroupState shape.
interface ColumnGroupStateItem {
  groupId: string;
  open: boolean;
}

export interface AgGridPersistedState {
  columnState?: ColumnState[];
  filterModel?: Record<string, unknown>;
  columnGroupState?: ColumnGroupStateItem[];
}

const DEFAULT_CONFIG: Partial<AgGridStateConfig> = {
  storageType: "session",
  persistColumnState: true,
  persistFilterState: true,
  persistSortState: true,
  persistColumnGroupState: true,
  debounceMs: 500,
};

/**
 * React hook for persisting and restoring AG-Grid state across sessions.
 *
 * Manages:
 * - Column state (width, position, visibility, pinning)
 * - Filter state
 * - Sort state
 * - Column group state (expanded/collapsed)
 *
 * @example
 * ```tsx
 * const { onGridReady, saveState, clearState } = useAgGridState({
 *   gridId: 'my-grid',
 *   storageType: 'local',
 *   onStateRestored: () => console.log('State restored'),
 * });
 *
 * return (
 *   <AgGridReact
 *     onGridReady={onGridReady}
 *     onColumnMoved={saveState}
 *     onColumnResized={saveState}
 *     onColumnVisible={saveState}
 *     onSortChanged={saveState}
 *     onFilterChanged={saveState}
 *     {...otherProps}
 *   />
 * );
 * ```
 */
export function useAgGridState(config: AgGridStateConfig): {
  onGridReady: (params: { api: GridApi }) => void;
  saveState: () => void;
  saveStateNow: () => void;
  clearState: () => void;
  restoreState: () => void;
  gridApi: GridApi | null;
} {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const {
    gridId,
    storageType = "session",
    persistColumnState,
    persistFilterState,
    persistSortState,
    persistColumnGroupState,
    debounceMs,
    onStateRestored,
    onStateSaved,
    onError,
  } = mergedConfig;

  const storage = useStorage(storageType);
  const gridApiRef = useRef<GridApi | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Generate storage key
  const getStorageKey = useCallback(
    (suffix: string) => `aggrid_${gridId}_${suffix}`,
    [gridId],
  );

  /**
   * Save the current grid state to storage
   */
  const saveState = useCallback(() => {
    if (!gridApiRef.current) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save operation
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const api = gridApiRef.current;
        if (!api) return;

        const state: AgGridPersistedState = {};

        // Save column state (includes sort if enabled)
        if (persistColumnState || persistSortState) {
          const columnState = api.getColumnState();
          if (columnState) {
            state.columnState = columnState;
          }
        }

        // Save filter state
        if (persistFilterState) {
          const filterModel = api.getFilterModel();
          if (filterModel && Object.keys(filterModel).length > 0) {
            state.filterModel = filterModel;
          }
        }

        // Save column group state
        if (persistColumnGroupState) {
          const columnGroupState = api.getColumnGroupState();
          if (columnGroupState) {
            state.columnGroupState = columnGroupState;
          }
        }

        // Persist to storage
        storage.setStorageItem(getStorageKey("state"), state);

        window.dispatchEvent(new CustomEvent("agGridStateSaved"));
        onStateSaved?.();
      } catch (error) {
        const err = error as Error;
        logger.error(
          `[useAgGridState] Error saving grid state for ${gridId}`,
          err,
        );
        onError?.(err);
      }
    }, debounceMs);
  }, [
    persistColumnState,
    persistSortState,
    persistFilterState,
    persistColumnGroupState,
    storage,
    getStorageKey,
    debounceMs,
    onStateSaved,
    onError,
    gridId,
  ]);

  /**
   * Restore grid state from storage
   */
  const restoreState = useCallback(() => {
    if (!gridApiRef.current) return;

    try {
      const api = gridApiRef.current;
      const state = storage.getStorageItem<AgGridPersistedState>(
        getStorageKey("state"),
      );

      if (!state) {
        logger.debug(`[useAgGridState] No saved state found for ${gridId}`);
        return;
      }

      // Restore column state (includes sort)
      if (state.columnState && (persistColumnState || persistSortState)) {
        api.applyColumnState({
          state: state.columnState,
          applyOrder: true,
        });
      }

      // Restore filter state
      if (state.filterModel && persistFilterState) {
        api.setFilterModel(state.filterModel);
      }

      // Restore column group state
      if (state.columnGroupState && persistColumnGroupState) {
        api.setColumnGroupState(state.columnGroupState);
      }

      logger.debug(`[useAgGridState] State restored for ${gridId}`);
      onStateRestored?.();
    } catch (error) {
      const err = error as Error;
      logger.error(
        `[useAgGridState] Error restoring grid state for ${gridId}`,
        err,
      );
      onError?.(err);
    }
  }, [
    storage,
    getStorageKey,
    persistColumnState,
    persistSortState,
    persistFilterState,
    persistColumnGroupState,
    gridId,
    onStateRestored,
    onError,
  ]);

  /**
   * Clear saved grid state from storage
   */
  const clearState = useCallback(() => {
    try {
      storage.removeStorageItem(getStorageKey("state"));
      logger.debug(`[useAgGridState] State cleared for ${gridId}`);
    } catch (error) {
      const err = error as Error;
      logger.error(
        `[useAgGridState] Error clearing grid state for ${gridId}`,
        err,
      );
      onError?.(err);
    }
  }, [storage, getStorageKey, gridId, onError]);

  /**
   * Handler for AG-Grid's onGridReady event
   */
  const onGridReady = useCallback(
    (params: { api: GridApi }) => {
      gridApiRef.current = params.api;
      // Restore state after grid is ready
      // Use a small delay to ensure column definitions are fully applied
      setTimeout(() => {
        restoreState();
      }, 100);
    },
    [restoreState],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    },
    [],
  );

  return {
    /**
     * Pass this to AG-Grid's onGridReady prop.
     * Initializes the grid API and restores saved state.
     */
    onGridReady,

    /**
     * Pass this to AG-Grid's state change event handlers
     * (onColumnMoved, onColumnResized, onColumnVisible, onSortChanged, onFilterChanged, etc.)
     * to automatically persist state changes.
     */
    saveState,

    /**
     * Manually save the current grid state.
     */
    saveStateNow: () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveState();
    },

    /**
     * Clear all saved grid state from storage.
     */
    clearState,

    /**
     * Manually restore grid state from storage.
     */
    restoreState,

    /**
     * Reference to the grid API.
     */
    gridApi: gridApiRef.current,
  };
}
